import React, { useEffect } from "react"
import { useField, useFormikContext } from "formik"

import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { ConfigQuery, DimensionDetailsForUserResponse, fetchDimensionDetailsForUser } from "../../createOrderApi"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"

import { getFormikFieldError, isAccountingDimensionKey } from "./helpers"
import { ExtendedDynamicConfigKey, DynamicConfigField } from "./formTypes"
import { handleAutoCompleteMode, useAutocompleteController } from "./useAutocompleteController"
import { useFormResetWatcher } from "./useFormResetWatcher"

function transformData(data: DimensionDetailsForUserResponse) {
    return data.accountDimension.costCenters.map((item) => ({
        id: item.value,
        name: item.name,
    }))
}

export type AccountingDimensionOption = Readonly<{
    id: string
    name: string
    dimensionName?: string
}>

type DimensionDetailsQueryParams = Readonly<{
    userId: number
    dimensionId: number
    search: string
    forceRefreshKey: number
}>

function fetchDimensionDetailsForUserWithRefreshKey(
    { forceRefreshKey: _forceRefreshKey, ...params }: DimensionDetailsQueryParams,
    axios: Parameters<typeof fetchDimensionDetailsForUser>[1],
    signal?: Parameters<typeof fetchDimensionDetailsForUser>[2],
) {
    return fetchDimensionDetailsForUser(params, axios, signal)
}

type DynamicAutocompleteProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
}

export function DynamicAutocomplete({ config, user }: DynamicAutocompleteProps) {
    if (!user) return <></>
    const { key, inputField, label, description } = config

    const [field, meta, helpers] = useField<AccountingDimensionOption | null>(key)
    const { submitCount } = useFormikContext<unknown>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(key)

    const mode = handleAutoCompleteMode(inputField?.mode)

    const {
        debouncedSearch,
        enabled,
        isLoadOnOpen,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    function getAutocompleteQueryConfig(key: ExtendedDynamicConfigKey, user: SelectedMainUserState, search: string) {
        if (isAccountingDimensionKey(key)) {
            const dimensionIdFromKey = Number(key.split("_")[2])
            return {
                fetchFn: fetchDimensionDetailsForUserWithRefreshKey,
                queryName: ConfigQuery.ACCOUNT_DIMENSION_DETAILS_FOR_USER,
                dependencies: {
                    userId: user.userId,
                    dimensionId: dimensionIdFromKey,
                    search,
                },
                select: transformData,
            }
        }

        return null
    }

    const queryConfig = getAutocompleteQueryConfig(key, user, debouncedSearch)
    if (!queryConfig) return <></>

    const {
        data: options = [],
        isError,
        isLoading,
        refetch,
    } = useApiQuery<DimensionDetailsQueryParams, DimensionDetailsForUserResponse, AccountingDimensionOption[]>(
        queryConfig.fetchFn,
        {
            queryName: queryConfig.queryName,
            dependencies: {
                ...queryConfig.dependencies,
                forceRefreshKey,
            },
            select: queryConfig.select,
            enabled,
        },
    )

    const dimensionName = label ?? description ?? ""
    const withDimensionName = (value: AccountingDimensionOption | null): AccountingDimensionOption | null =>
        value ? { ...value, dimensionName } : value

    useEffect(() => {
        // Always fetch once on mount (regardless of mode from config).
        // After that, all query behavior is controlled by `enabled` from useAutocompleteController.
        if (!field.value) void refetch()
    }, [])

    useEffect(() => {
        // Auto-fill when there is exactly one option and the field is empty.
        // Important: only do this for the initial (non-search) result set, so we don't auto-select
        // a single match while the user is typing and keep the existing query conditions intact.
        if (debouncedSearch.trim() === "" && options.length === 1 && !field.value) {
            helpers.setValue(withDimensionName(options[0]))
        }
    }, [debouncedSearch, field.value, helpers, options])

    useEffect(() => {
        if (inputField?.predefined && !field.value) {
            helpers.setValue(
                withDimensionName({ id: String(inputField.predefined.value), name: inputField.predefined.text || "" }),
            )
        }
    }, [])

    const handleChange = (_event: React.SyntheticEvent, value: AccountingDimensionOption | null) => {
        helpers.setValue(withDimensionName(value))
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }

    return (
        <ApiQueryAutocomplete
            isLoading={enabled && isLoading}
            isError={isError}
            options={options}
            getOptionLabel={(option) => option.name || ""}
            value={field.value || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={handleOpen}
            onClose={handleClose}
            size="small"
            slotProps={{
                textField: formikErrorProps,
            }}
            label={`${label || description}${inputField?.isRequired ? " *" : ""}`}
            isOptionEqualToValue={(option, value) => {
                if (!value || !option) return false
                return option.id === value.id
            }}
            filterOptions={(x) => x}
            readOnly={options.length === 1}
        />
    )
}
